// Reine clientseitige Geometrie für Bohrpunkte.de.
// Keine React-Abhängigkeit, damit die Logik testbar bleibt (siehe scripts/calc.test.js).
// Alle Maße in Zentimetern, sofern nicht anders angegeben.

// Auf 1 Nachkommastelle runden (mm-genau), aber saubere Ganzzahlen ohne ".0".
export function round1(n) {
  if (!isFinite(n)) return n;
  return Math.round(n * 10) / 10;
}

// Zahl als deutsches cm-Maß formatieren, z. B. "33,3 cm".
export function fmtCm(n) {
  const r = round1(n);
  const s = (Number.isInteger(r) ? r.toString() : r.toFixed(1)).replace(".", ",");
  return `${s} cm`;
}

function toNum(v) {
  if (v === "" || v === null || v === undefined) return NaN;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  return n;
}

// ----------------------------------------------------------------------------
// 1. Bilder gleichmäßig aufhängen (PRD §7.3 + §13)
// ----------------------------------------------------------------------------
// Eingaben:
//   wallWidth   Wandbreite (cm)
//   count       Anzahl Bilder
//   pictureWidth Bildbreite (cm)
//   pictureHeight Bildhöhe (cm, optional, für Bohrhöhe + Skizze)
//   margin      gewünschter Außenabstand links/rechts (cm, optional)
//   centerFromFloor gewünschte Bildmitte ab Boden (cm, optional)
//   hangerFromTop Abstand der Aufhängung zur Oberkante (cm, optional)
export function calcEvenPictures(input) {
  const wallWidth = toNum(input.wallWidth);
  const count = Math.round(toNum(input.count));
  const pictureWidth = toNum(input.pictureWidth);
  const pictureHeight = toNum(input.pictureHeight);
  const margin = input.margin === "" || input.margin === undefined ? null : toNum(input.margin);
  const centerFromFloor =
    input.centerFromFloor === "" || input.centerFromFloor === undefined ? null : toNum(input.centerFromFloor);
  const hangerFromTop =
    input.hangerFromTop === "" || input.hangerFromTop === undefined ? null : toNum(input.hangerFromTop);

  const warnings = [];
  const valid =
    isFinite(wallWidth) && wallWidth > 0 && isFinite(count) && count >= 1 && isFinite(pictureWidth) && pictureWidth > 0;

  if (!valid) {
    return { ok: false, warnings: [], pictures: [], totalWidth: NaN };
  }

  const totalWidth = count * pictureWidth;
  if (totalWidth > wallWidth) {
    warnings.push("Die Bilder sind zusammen breiter als die Wand.");
  }

  let leftEdges = [];
  let gap = null;
  let usedMargin = null;

  if (margin !== null && isFinite(margin)) {
    // Außenabstand fest vorgegeben (PRD §13, zweite Formel).
    usedMargin = margin;
    const freeInner = wallWidth - 2 * margin - totalWidth;
    const innerGaps = count - 1;
    gap = innerGaps > 0 ? freeInner / innerGaps : 0;
    if (freeInner < 0) {
      warnings.push("Bei diesem Außenabstand passen die Bilder nicht nebeneinander an die Wand.");
    } else if (gap < 0) {
      warnings.push("Der Abstand zwischen den Bildern wäre negativ.");
    }
    for (let n = 1; n <= count; n++) {
      const left = margin + (n - 1) * (pictureWidth + gap);
      leftEdges.push(left);
    }
  } else {
    // Gleichmäßige Verteilung inkl. Randlücken (PRD §13, erste Formel).
    const freeSpace = wallWidth - totalWidth;
    const gaps = count + 1;
    gap = freeSpace / gaps;
    usedMargin = gap;
    if (gap < 0) {
      warnings.push("Der Abstand zwischen den Bildern wäre negativ.");
    }
    for (let n = 1; n <= count; n++) {
      const left = gap + (n - 1) * (pictureWidth + gap);
      leftEdges.push(left);
    }
  }

  // Bohrhöhe (vertikal), falls genug Angaben vorhanden.
  let drillHeight = null;
  let topEdge = null;
  if (centerFromFloor !== null && isFinite(centerFromFloor) && isFinite(pictureHeight) && pictureHeight > 0) {
    topEdge = centerFromFloor + pictureHeight / 2;
    const fromTop = hangerFromTop !== null && isFinite(hangerFromTop) ? hangerFromTop : 0;
    drillHeight = topEdge - fromTop;
    if (drillHeight < 0) {
      warnings.push("Die Bohrhöhe läge unter dem Boden. Prüfe Bildmitte und Bildhöhe.");
    }
  }

  const pictures = leftEdges.map((left, i) => ({
    index: i + 1,
    left: round1(left),
    center: round1(left + pictureWidth / 2),
    right: round1(left + pictureWidth),
    drillX: round1(left + pictureWidth / 2),
  }));

  return {
    ok: true,
    warnings,
    wallWidth,
    count,
    pictureWidth,
    pictureHeight: isFinite(pictureHeight) ? pictureHeight : null,
    totalWidth: round1(totalWidth),
    gap: round1(gap),
    margin: round1(usedMargin),
    leftMargin: round1(leftEdges[0]),
    rightMargin: round1(wallWidth - (leftEdges[leftEdges.length - 1] + pictureWidth)),
    pictures,
    drillHeight: drillHeight !== null ? round1(drillHeight) : null,
    topEdge: topEdge !== null ? round1(topEdge) : null,
    centerFromFloor: centerFromFloor !== null && isFinite(centerFromFloor) ? centerFromFloor : null,
  };
}

// ----------------------------------------------------------------------------
// 2. Einzelnes Bild auf Höhe bringen (PRD §7.4)
// ----------------------------------------------------------------------------
// Eingaben:
//   centerFromFloor gewünschte Bildmitte ab Boden (cm)
//   pictureHeight   Bildhöhe (cm)
//   hangerFromTop   Abstand Aufhängung zur Oberkante (cm)
export function calcSinglePicture(input) {
  const centerFromFloor = toNum(input.centerFromFloor);
  const pictureHeight = toNum(input.pictureHeight);
  const hangerFromTop = toNum(input.hangerFromTop);

  const warnings = [];
  const valid =
    isFinite(centerFromFloor) && centerFromFloor > 0 && isFinite(pictureHeight) && pictureHeight > 0 && isFinite(hangerFromTop);
  if (!valid) {
    return { ok: false, warnings: [] };
  }

  if (hangerFromTop > pictureHeight) {
    warnings.push("Der Abstand der Aufhängung zur Oberkante ist größer als die Bildhöhe. Bitte prüfen.");
  }

  const topEdge = centerFromFloor + pictureHeight / 2;
  const bottomEdge = centerFromFloor - pictureHeight / 2;
  const drillHeight = topEdge - hangerFromTop;

  if (bottomEdge < 0) {
    warnings.push("Die Bildunterkante läge unter dem Boden. Prüfe Bildmitte und Bildhöhe.");
  }
  if (drillHeight < 0) {
    warnings.push("Die Bohrhöhe läge unter dem Boden.");
  }

  return {
    ok: true,
    warnings,
    centerFromFloor,
    pictureHeight,
    hangerFromTop,
    topEdge: round1(topEdge),
    bottomEdge: round1(bottomEdge),
    drillHeight: round1(drillHeight),
  };
}

// ----------------------------------------------------------------------------
// 3. Regal mittig montieren (PRD §7.5)
// ----------------------------------------------------------------------------
// Eingaben:
//   wallWidth   Wandbreite (cm)
//   shelfWidth  Regalbreite (cm)
//   holeSpacing Lochabstand am Regal (cm)
//   height      gewünschte Höhe ab Boden (cm, optional, für Ausgabe)
//   position    "center" | "custom"
//   fromLeft    Abstand der linken Regalkante von links (cm, nur bei custom)
export function calcShelf(input) {
  const wallWidth = toNum(input.wallWidth);
  const shelfWidth = toNum(input.shelfWidth);
  const holeSpacing = toNum(input.holeSpacing);
  const height = input.height === "" || input.height === undefined ? null : toNum(input.height);
  const position = input.position === "custom" ? "custom" : "center";
  const fromLeft = toNum(input.fromLeft);

  const warnings = [];
  const valid =
    isFinite(wallWidth) && wallWidth > 0 && isFinite(shelfWidth) && shelfWidth > 0 && isFinite(holeSpacing) && holeSpacing > 0;
  if (!valid) {
    return { ok: false, warnings: [] };
  }

  if (shelfWidth > wallWidth) {
    warnings.push("Das Regal ist breiter als die Wand.");
  }
  if (holeSpacing > shelfWidth) {
    warnings.push("Der Lochabstand ist größer als die Regalbreite.");
  }

  // Linke Regalkante bestimmen.
  let shelfLeft;
  if (position === "custom" && isFinite(fromLeft)) {
    shelfLeft = fromLeft;
  } else {
    shelfLeft = (wallWidth - shelfWidth) / 2;
  }
  if (shelfLeft < 0 || shelfLeft + shelfWidth > wallWidth) {
    warnings.push("Das Regal ragt an dieser Position über die Wandkante hinaus.");
  }

  const shelfCenter = shelfLeft + shelfWidth / 2;
  const wallCenter = wallWidth / 2;
  // Bohrlöcher symmetrisch zur Regalmitte.
  const hole1 = shelfCenter - holeSpacing / 2;
  const hole2 = shelfCenter + holeSpacing / 2;

  return {
    ok: true,
    warnings,
    wallWidth,
    shelfWidth,
    holeSpacing,
    height: height !== null && isFinite(height) ? height : null,
    position,
    shelfLeft: round1(shelfLeft),
    shelfRight: round1(shelfLeft + shelfWidth),
    shelfCenter: round1(shelfCenter),
    wallCenter: round1(wallCenter),
    hole1: round1(hole1),
    hole2: round1(hole2),
    controlSpacing: round1(hole2 - hole1),
  };
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

// ----------------------------------------------------------------------------
// 4. Möbelgriffe bohren (PRD §7.6)
// ----------------------------------------------------------------------------
// Eingaben:
//   doorWidth   Tür-/Frontbreite (cm)
//   doorHeight  Tür-/Fronthöhe (cm)
//   spacing     Griff-Lochabstand (cm)
//   orient      "h" | "v" (horizontal / vertikal)
//   centerX     gewünschte Griffmitte von links (cm, optional; sonst Frontmitte)
//   centerY     gewünschte Griffmitte von oben (cm, optional; sonst Frontmitte)
// Bei horizontalem Griff sitzen die beiden Löcher links/rechts auf gleicher Höhe (centerY).
// Bei vertikalem Griff sitzen sie oben/unten auf gleicher Spalte (centerX).
export function calcGrip(input) {
  const doorWidth = toNum(input.doorWidth);
  const doorHeight = toNum(input.doorHeight);
  const spacing = toNum(input.spacing);
  const orient = input.orient === "v" ? "v" : "h";

  const warnings = [];
  const valid =
    isFinite(doorWidth) && doorWidth > 0 && isFinite(doorHeight) && doorHeight > 0 && isFinite(spacing) && spacing >= 0;
  if (!valid) {
    return { ok: false, warnings: [] };
  }

  let centerX = toNum(input.centerX);
  let centerY = toNum(input.centerY);
  if (!isFinite(centerX)) centerX = doorWidth / 2;
  if (!isFinite(centerY)) centerY = doorHeight / 2;

  if (orient === "h") {
    if (spacing > doorWidth) {
      warnings.push("Der Lochabstand ist größer als die Frontbreite.");
    }
    centerX = clamp(centerX, spacing / 2, doorWidth - spacing / 2);
    centerY = clamp(centerY, 0, doorHeight);
    const hole1 = centerX - spacing / 2;
    const hole2 = centerX + spacing / 2;
    return {
      ok: true,
      warnings,
      orient,
      doorWidth,
      doorHeight,
      spacing,
      centerX,
      centerY,
      hole1: round1(hole1),
      hole2: round1(hole2),
      // Achse, an der gebohrt wird (von links): hole1/hole2.
      axisFromTop: round1(centerY), // Höhe der Lochreihe von der Oberkante
      edgeStart: round1(hole1), // Abstand erstes Loch zur linken Kante
      edgeEnd: round1(doorWidth - hole2), // Abstand letztes Loch zur rechten Kante
    };
  }

  // vertikal
  if (spacing > doorHeight) {
    warnings.push("Der Lochabstand ist größer als die Fronthöhe.");
  }
  centerY = clamp(centerY, spacing / 2, doorHeight - spacing / 2);
  centerX = clamp(centerX, 0, doorWidth);
  const hole1 = centerY - spacing / 2;
  const hole2 = centerY + spacing / 2;
  return {
    ok: true,
    warnings,
    orient,
    doorWidth,
    doorHeight,
    spacing,
    centerX,
    centerY,
    hole1: round1(hole1), // von oben
    hole2: round1(hole2), // von oben
    axisFromSide: round1(centerX), // Abstand der Lochspalte zur Seitenkante
    edgeStart: round1(hole1), // Abstand oberes Loch zur Oberkante
    edgeEnd: round1(doorHeight - hole2), // Abstand unteres Loch zur Unterkante
  };
}

// ----------------------------------------------------------------------------
// 5. Gardinenstange ausrichten (PRD §7.7)
// ----------------------------------------------------------------------------
// Eingaben:
//   windowWidth     Fensterbreite (cm)
//   windowHeight    Fensterhöhe (cm, optional, nur für die Skizze)
//   overhang        gewünschter Überstand je Seite (cm)
//   bracketFromEdge Halterabstand von der Fensterkante nach außen (cm)
//   heightAboveWindow Höhe der Stange über der Fensteroberkante (cm)
//   wallWidth       Wandbreite (cm, optional; ermöglicht Halterposition ab Wandkante)
export function calcCurtain(input) {
  const windowWidth = toNum(input.windowWidth);
  const windowHeight = toNum(input.windowHeight);
  const overhang = toNum(input.overhang);
  const bracketFromEdge = toNum(input.bracketFromEdge);
  const heightAboveWindow = toNum(input.heightAboveWindow);
  const wall = input.wallWidth === "" || input.wallWidth === undefined ? null : toNum(input.wallWidth);

  const warnings = [];
  const valid =
    isFinite(windowWidth) && windowWidth > 0 && isFinite(overhang) && overhang >= 0 && isFinite(bracketFromEdge) && bracketFromEdge >= 0;
  if (!valid) {
    return { ok: false, warnings: [] };
  }

  const rodLength = windowWidth + 2 * overhang;
  if (bracketFromEdge > overhang) {
    warnings.push("Der Halter sitzt außerhalb der Stange. Überstand oder Halterabstand anpassen.");
  }

  let windowLeft = null;
  let bracketLeftFromWall = null;
  let bracketRightFromWall = null;
  if (wall !== null && isFinite(wall)) {
    windowLeft = (wall - windowWidth) / 2;
    if (windowLeft < 0) {
      warnings.push("Das Fenster ist breiter als die Wand.");
    }
    bracketLeftFromWall = windowLeft - bracketFromEdge;
    bracketRightFromWall = windowLeft + windowWidth + bracketFromEdge;
  }

  return {
    ok: true,
    warnings,
    windowWidth,
    windowHeight: isFinite(windowHeight) && windowHeight > 0 ? windowHeight : null,
    overhang,
    bracketFromEdge,
    heightAboveWindow: isFinite(heightAboveWindow) ? heightAboveWindow : 0,
    wall: wall !== null && isFinite(wall) ? wall : null,
    rodLength: round1(rodLength),
    windowLeft: windowLeft !== null ? round1(windowLeft) : null,
    bracketFromEdgeOut: round1(bracketFromEdge), // Halter je außerhalb der Fensterkante
    bracketLeftFromWall: bracketLeftFromWall !== null ? round1(bracketLeftFromWall) : null,
    bracketRightFromWall: bracketRightFromWall !== null ? round1(bracketRightFromWall) : null,
  };
}
